import { useRef, useEffect, useState, useCallback } from "react";
import { Stage, Layer, Rect, Line, Text, Group, Transformer } from "react-konva";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Grid3x3 } from "lucide-react";

const GRID = 20;
const PALETTE = ["#FDE047", "#3B82F6", "#34D399", "#F472B6", "#FB923C", "#A78BFA"];
const snap = (v) => Math.round(v / GRID) * GRID;

let idc = 0;
const nid = () => `room_${Date.now()}_${idc++}`;

function RoomShape({ room, isSelected, onSelect, onChange }) {
  const ref = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected && trRef.current && ref.current) {
      trRef.current.nodes([ref.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <Group
        ref={ref}
        x={room.x}
        y={room.y}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={(e) => onChange({ ...room, x: snap(e.target.x()), y: snap(e.target.y()) })}
        onTransformEnd={() => {
          const node = ref.current;
          const sx = node.scaleX();
          const sy = node.scaleY();
          node.scaleX(1);
          node.scaleY(1);
          onChange({
            ...room,
            x: snap(node.x()),
            y: snap(node.y()),
            w: Math.max(GRID * 2, snap(room.w * sx)),
            h: Math.max(GRID * 2, snap(room.h * sy)),
          });
        }}
      >
        <Rect
          width={room.w}
          height={room.h}
          fill={`${room.color}22`}
          stroke={isSelected ? "#FDE047" : room.color}
          strokeWidth={isSelected ? 2.5 : 1.5}
        />
        <Text text={room.name} x={6} y={6} fontSize={12} fontFamily="IBM Plex Mono" fill="#F8FAFC" />
        <Text
          text={`${Math.round((room.w / GRID) * 2)}' x ${Math.round((room.h / GRID) * 2)}'`}
          x={6}
          y={room.h - 18}
          fontSize={10}
          fontFamily="IBM Plex Mono"
          fill="#94A3B8"
        />
      </Group>
      {isSelected && (
        <Transformer
          ref={trRef}
          rotateEnabled={false}
          borderStroke="#FDE047"
          anchorStroke="#FDE047"
          anchorFill="#030712"
          boundBoxFunc={(oldBox, newBox) => (newBox.width < 40 || newBox.height < 40 ? oldBox : newBox)}
        />
      )}
    </>
  );
}

export default function BlueprintCanvas({ value, onChange, height = 460 }) {
  const containerRef = useRef();
  const [width, setWidth] = useState(800);
  const [selectedId, setSelectedId] = useState(null);
  const rooms = value?.rooms || [];

  useEffect(() => {
    const update = () => setWidth(containerRef.current?.offsetWidth || 800);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const setRooms = useCallback(
    (next) => onChange({ ...(value || {}), rooms: next, walls: value?.walls || [] }),
    [onChange, value]
  );

  const addRoom = () => {
    const i = rooms.length;
    const room = {
      id: nid(),
      name: `Room ${i + 1}`,
      x: snap(40 + (i % 4) * 30),
      y: snap(40 + (i % 4) * 30),
      w: 160,
      h: 120,
      color: PALETTE[i % PALETTE.length],
    };
    setRooms([...rooms, room]);
    setSelectedId(room.id);
  };

  const updateRoom = (r) => setRooms(rooms.map((x) => (x.id === r.id ? r : x)));
  const deleteSelected = () => {
    if (!selectedId) return;
    setRooms(rooms.filter((r) => r.id !== selectedId));
    setSelectedId(null);
  };

  const gridLines = [];
  for (let i = 0; i < width / GRID; i++)
    gridLines.push(<Line key={`v${i}`} points={[i * GRID, 0, i * GRID, height]} stroke="rgba(59,130,246,0.08)" strokeWidth={1} />);
  for (let i = 0; i < height / GRID; i++)
    gridLines.push(<Line key={`h${i}`} points={[0, i * GRID, width, i * GRID]} stroke="rgba(59,130,246,0.08)" strokeWidth={1} />);

  return (
    <div data-testid="blueprint-canvas">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" onClick={addRoom} data-testid="add-room-btn" className="rounded-sm bg-primary text-black hover:bg-primary/90">
          <Plus className="mr-1 h-4 w-4" /> Add Room
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={deleteSelected}
          disabled={!selectedId}
          data-testid="delete-room-btn"
          className="rounded-sm border-border"
        >
          <Trash2 className="mr-1 h-4 w-4" /> Delete
        </Button>
        <span className="ml-auto flex items-center gap-1.5 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">
          <Grid3x3 className="h-3.5 w-3.5" /> 1 sq = 2 ft · {rooms.length} rooms
        </span>
      </div>
      <div ref={containerRef} className="overflow-hidden border border-border bg-[#050a16]">
        <Stage
          width={width}
          height={height}
          onMouseDown={(e) => {
            if (e.target === e.target.getStage()) setSelectedId(null);
          }}
        >
          <Layer listening={false}>{gridLines}</Layer>
          <Layer>
            {rooms.map((room) => (
              <RoomShape
                key={room.id}
                room={room}
                isSelected={room.id === selectedId}
                onSelect={() => setSelectedId(room.id)}
                onChange={updateRoom}
              />
            ))}
          </Layer>
        </Stage>
      </div>
      <p className="mt-2 font-mono2 text-xs text-muted-foreground">
        Drag rooms to reposition · select to resize or delete · snaps to grid.
      </p>
    </div>
  );
}
